import { inject, Injectable } from '@angular/core';
import * as Sentry from '@sentry/angular';
import { LoggingService } from '../logging.service';
import { CashRegisterStateChange, CASH_REGISTERS_COLLECTION } from './cash-register-sync.models';

/** Seq `Category` shared by every event of this feature. */
export const CASH_REGISTER_SYNC_LOG_CATEGORY = 'CashRegisterFirestoreSync';

/**
 * Structured Seq logging for cash register replica synchronization.
 *
 * Every replicated change gets exactly one terminal event — succeeded, failed
 * or skipped-as-stale — sharing the same property set, so a Seq query on
 * `Category = 'CashRegisterFirestoreSync'` reconciles the two stores on its
 * own. A failure additionally carries what the replica should have held, so
 * the write can be replayed.
 *
 * Sensitive data is not logged: the expected state contains the register's
 * business fields only — never `metadata` (user profile) — and the user/tenant
 * context is added by {@link LoggingService} itself.
 */
@Injectable({ providedIn: 'root' })
export class CashRegisterSyncLogger {
  private readonly loggingService = inject(LoggingService);

  /**
   * The change reached Firestore: the backend acknowledged the write. Queued
   * writes are durable, so while the device is offline this lands on
   * reconnect — `DurationMs` (queued → acknowledged) shows how long it was
   * deferred.
   */
  logSyncSucceeded(change: CashRegisterStateChange, created: boolean, queuedAt: Date): void {
    this.loggingService.info(
      'Cash register Firestore sync succeeded: {Operation} on cash register {CashRegisterId} replicated after {NativeServiceOperation}',
      {
        ...this.describe(change),
        NativeServiceSucceeded: true,
        FirestoreSucceeded: true,
        FirestoreOutcome: created ? 'Created' : 'Updated',
        DurationMs: Date.now() - queuedAt.getTime()
      }
    );
  }

  /** The Native Service succeeded but the Firestore write did not. */
  logSyncFailure(change: CashRegisterStateChange, error: unknown): void {
    const properties = {
      ...this.describe(change),
      NativeServiceSucceeded: true,
      FirestoreSucceeded: false,
      ExpectedState: this.expectedState(change),
      ErrorCode: (error as any)?.code,
      ErrorMessage: (error as any)?.message ?? String(error),
      ErrorStack: (error as any)?.stack
    };

    this.loggingService.error(
      'Cash register Firestore sync failed: {Operation} on cash register {CashRegisterId} after {NativeServiceOperation} succeeded',
      properties
    );
    console.error(
      `[CashRegisterFirestoreSync] ${change.operation} on cash register ${change.cashRegisterId} was not replicated to Firestore`,
      error
    );
    Sentry.captureException(error, {
      tags: { Category: CASH_REGISTER_SYNC_LOG_CATEGORY, Operation: change.operation },
      extra: { CashRegisterId: change.cashRegisterId, CorrelationId: change.correlationId }
    });
  }

  /**
   * The replica already held a newer state for the same scope, so the write was
   * skipped on purpose. Not a failure, but recorded so a reconciliation can
   * verify the ordering decision.
   */
  logStaleChangeSkipped(change: CashRegisterStateChange, existingStamp: Date | undefined): void {
    this.loggingService.warning(
      'Cash register Firestore sync skipped a stale {Operation} on cash register {CashRegisterId}',
      {
        ...this.describe(change),
        NativeServiceSucceeded: true,
        FirestoreSucceeded: true,
        FirestoreOutcome: 'SkippedStale',
        ExistingStamp: existingStamp?.toISOString()
      }
    );
  }

  private describe(change: CashRegisterStateChange): Record<string, unknown> {
    return {
      Category: CASH_REGISTER_SYNC_LOG_CATEGORY,
      Operation: change.operation,
      Scope: change.scope,
      CashRegisterId: change.cashRegisterId,
      CashRegisterCode: change.state?.code,
      CorrelationId: change.correlationId,
      NativeServiceOperation: change.primaryOperation,
      FirestoreOperation: `set ${CASH_REGISTERS_COLLECTION}/${change.cashRegisterId} mergeFields=[${change.changedFields.join(',')}]`,
      FirestoreCollection: CASH_REGISTERS_COLLECTION,
      FirestoreDocumentId: change.cashRegisterId,
      OccurredAt: change.occurredAt?.toISOString?.() ?? String(change.occurredAt),
      ChangedFields: change.changedFields,
      Online: typeof navigator !== 'undefined' ? navigator.onLine : undefined
    };
  }

  /** The fields the replica should hold after the change, bounded like every other Seq payload. */
  private expectedState(change: CashRegisterStateChange): unknown {
    const { metadata, ...businessState } = (change.state ?? {}) as any;
    const subset: Record<string, unknown> = {};
    for (const field of change.changedFields) {
      subset[field] = businessState[field];
    }
    return this.loggingService.truncateBody(subset);
  }
}
